import React, { useState, useEffect } from 'react';
import StatsSection from '../components/StatsSection';
import CallLogsTable from '../components/CallLogsTable';
import { fetchCallLogs, fetchCallStats } from '../services/api';
import useLiveCalls from '../hooks/useLiveCalls';
import { calculateDateRange } from '../utils/helpers';
import '../styles/App.css';
import FilterSection from '../components/FilterSection';

const getTodayString = () => {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

function Dashboard() {
  const live = useLiveCalls();
  const [callLogs, setCallLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    dateRange: '',
    start_date: null,
    end_date: null,
    phoneCountry: '',
    calleeCountry: '',
    callType: '',
    receiver: '',
    caller: '',
    statusCode: ''
  });
  const [pagination, setPagination] = useState({
    current_page: 1,
    per_page: 10,
    total_pages: 1,
    total_items: 0
  });
  const [stats, setStats] = useState({
    total_calls: 0,
    completed_calls: 0,
    failed_calls: 0,
    success_rate: 0
  });

  useEffect(() => {
    loadCallLogs();
  }, [
    pagination.current_page,
    filters.dateRange,
    filters.start_date,
    filters.end_date,
    filters.callType,
    filters.statusCode,
    filters.receiver,
    filters.caller
  ]);

  useEffect(() => {
    loadStats();
  }, [
    filters.dateRange,
    filters.start_date,
    filters.end_date
  ]);

  const startItem = (pagination.current_page - 1) * pagination.per_page + 1;
  const endItem = Math.min(pagination.current_page * pagination.per_page, pagination.total_items);

  const loadCallLogs = async () => {
    setLoading(true);
    setError(null);
    try {
      let dateRange = {};

      if (filters.dateRange === 'Custom Date') {
        if (filters.start_date) {
          // end_date is optional; the API defaults it to today
          dateRange = {
            start_date: filters.start_date,
            ...(filters.end_date && { end_date: filters.end_date })
          };
        }
      } else if (
        filters.dateRange === 'Last 7 Days' ||
        filters.dateRange === 'Last 30 Days'
      ) {
        dateRange = calculateDateRange(filters.dateRange);
      }
   
      const params = {
        page: pagination.current_page,
        per_page: pagination.per_page,
        ...(filters.receiver && { receiver: filters.receiver }),
        ...(filters.caller && { caller: filters.caller }),
        ...(filters.callType && { call_type: filters.callType }),
        ...(filters.statusCode && { call_status: filters.statusCode }),
        ...dateRange
      };

      console.log('Fetching with params:', params);

      const response = await fetchCallLogs(params);
      
      console.log('API Response:', response);

      if (response.success) {
        setCallLogs(response.data || []);
        if (response.pagination) {
          setPagination(response.pagination);
        }
      } else {
        setError('Failed to load call logs');
        setCallLogs([]);
      }
    } catch (error) {
      console.error('Error loading call logs:', error);
      setError(error.message || 'An error occurred while loading call logs');
      setCallLogs([]);
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      let dateRange = {};

      if (filters.dateRange === 'Custom Date') {
        if (filters.start_date) {
          // the stats API needs both dates, so default end_date to today here
          dateRange = {
            start_date: filters.start_date,
            end_date: filters.end_date || getTodayString()
          };
        }
      } else if (
        filters.dateRange === 'Last 7 Days' ||
        filters.dateRange === 'Last 30 Days'
      ) {
        dateRange = calculateDateRange(filters.dateRange);
      }

      const response = await fetchCallStats(dateRange);

      console.log('Stats Response:', response);
      
      if (response.success) {
        setStats({
          total_calls: response.data.total_calls || 0,
          completed_calls: response.data.completed_calls || 0,
          failed_calls: response.data.failed_calls || 0,
          success_rate: response.data.success_rate || 0
        });
      }
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  };

  const handleFilterChange = (field, value) => {
    setPagination(prev => ({ ...prev, current_page: 1 }));

    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleReset = () => {
    setFilters({
      dateRange: 'Select Date',
      start_date: '',
      end_date: '',
      phoneCountry: '',
      calleeCountry: '',
      callType: '',
      receiver: '',
      caller: '',
      statusCode: ''
    });
    setPagination(prev => ({ ...prev, current_page: 1 }));
    loadCallLogs();
  };

  const handleSearch = () => {
    setPagination(prev => ({ ...prev, current_page: 1 }));
    loadCallLogs();
  };

  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, current_page: newPage }));
  };

  return (
    <div className="content">
      <div className="main-content">
        <h1 className="page-title">Calls</h1>
        
        {error && (
          <div style={{
            padding: '12px 16px',
            backgroundColor: '#f8d7da',
            color: '#721c24',
            borderRadius: '4px',
            marginBottom: '16px',
            border: '1px solid #f5c6cb'
          }}>
            {error}
          </div>
        )}
        
        <FilterSection
          filters={filters}
          onFilterChange={handleFilterChange}
          onReset={handleReset}
          onSearch={handleSearch}
        />

        <StatsSection stats={stats} live={live} />

        <CallLogsTable 
          callLogs={callLogs}
          loading={loading}
        />

        {pagination.total_items > 0 && (
          <div style={{
            marginTop: '16px',
            padding: '16px',
            backgroundColor: '#fff',
            borderRadius: '4px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
          }}>
            <div style={{ fontSize: '14px', color: '#666' }}>
              Showing {startItem}–{endItem} of {pagination.total_items} calls
              (Page {pagination.current_page} of {pagination.total_pages})
            </div>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button 
                onClick={() => handlePageChange(pagination.current_page - 1)}
                disabled={!pagination.has_prev}
                style={{
                  padding: '6px 12px',
                  backgroundColor: pagination.has_prev ? '#fff' : '#f5f5f5',
                  border: '1px solid #d0d0d0',
                  borderRadius: '4px',
                  cursor: pagination.has_prev ? 'pointer' : 'not-allowed',
                  fontSize: '13px'
                }}
              >
                Previous
              </button>
              <button 
                onClick={() => handlePageChange(pagination.current_page + 1)}
                disabled={!pagination.has_next}
                style={{
                  padding: '6px 12px',
                  backgroundColor: pagination.has_next ? '#fff' : '#f5f5f5',
                  border: '1px solid #d0d0d0',
                  borderRadius: '4px',
                  cursor: pagination.has_next ? 'pointer' : 'not-allowed',
                  fontSize: '13px'
                }}
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

    </div>
  );
}

export default Dashboard;